export interface UseVoiceRecognitionParams {
  onTranscriptFragment: ReceiveTranscriptFragment
}

/**
 * Common interface that all providers must implement
 * We define a "fragment" as a word/utterance that an API sends back
 * Fragments can be "final" -- they are certain and won't change, or
 * "interim" -- they are not final, and the API may send back the same fragment
 * with a different value than before
 *
 * We invoke this call back for 1) All interim results 2) the first final result of a fragment
 *
 * This allows us to render interim results live and not do extra updates once a fragment is final
 */
export type ReceiveTranscriptFragment = (fragment: TranscriptFragment) => void

export type Word = {
  text: string
  isFinal: boolean
}

export interface TranscriptFragment {
  turn: number
  isFinal: boolean
  isFormatted: boolean
  content: string
  words: Word[]
}

export const SPEECH_RECOGNITION_EXTERNAL_ERROR =
  'Speech recognition is not currently available. Please try again later.'

/**
 * State transition diagram for speech recognition providers:
 *
 *                                  ┌─────────┐
 *                                  │         │
 *                                  │  IDLE   │
 *                                  │         │
 *                                  └────┬────┘
 *                                       │
 *                                       │ start-session
 *                                       ▼
 *                              ┌──────────────┐
 *                              │              │◄─────┐
 *                              │ SESSION      │      │
 *                              │ READY        │      │
 *                              │              │      │
 *                              └──────┬───────┘      │
 *                                     │              │
 *                                     │              │
 *                                     │              │
 *                                     │ start-recording
 *                                     ▼              │
 *                              ┌──────────────┐      │
 *                              │              │      │
 *                              │ RECORDING    │      │
 *                              │              │      │
 *                              └──────┬───────┘      │
 *                                     │              │
 *                                     │ stop-recording
 *                                     │              │
 *                                     └──────────────┘
 *
 * Notes:
 * - Error state can be reached from any state
 * - Reset action returns to IDLE from any state
 * - Invalid transitions result in error state
 */

// Unified action types - same across all providers
export type SpeechRecognitionAction =
  | { type: 'reset' }
  | { type: 'start-session' }
  | {
      type: 'start-recording'
      onTranscriptFragment: ReceiveTranscriptFragment
    }
  | { type: 'stop-recording' }
  | {
      type: 'error-occurred'
      error: string
    }

export enum ProviderType {
  WebSpeech = 'webspeech',
  AssemblyAI = 'assemblyai',
}
